import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2, Mail, MessageCircle, Link2Off } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useShareLink, useRevokeShareLink } from "@/hooks/use-wholesale";
import { buildShareMessage, viberForwardUrl, mailtoUrl } from "@/lib/wholesale-share-message";
import { fmtUnixDate } from "@/lib/wholesale-format";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: { id: string; number: string; partnerName: string; total: number };
  /** Rok plaćanja u unix sekundama; `null` kad nije poznat. */
  dueAt: number | null;
  partnerEmail: string;
};

export function ShareOrderDialog({ open, onOpenChange, order, dueAt, partnerEmail }: Props) {
  const share = useShareLink();
  const revoke = useRevokeShareLink();
  const [link, setLink] = useState<{ url: string; expiresAt: number } | null>(null);

  // Link se traži tek kad se dialog otvori; server vraća postojeći dok god važi.
  useEffect(() => {
    if (!open) return;
    setLink(null);
    share
      .mutateAsync(order.id)
      .then((res) => setLink({ url: res.url, expiresAt: res.expiresAt }))
      .catch((e: any) => toast.error(e?.message || "Link nije napravljen"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order.id]);

  const message = link
    ? buildShareMessage({ number: order.number, partnerName: order.partnerName, total: order.total, dueAt, url: link.url })
    : "";

  async function copy(text: string, okMessage: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(okMessage);
    } catch {
      toast.error("Kopiranje nije uspjelo — označite tekst i kopirajte ručno");
    }
  }

  async function handleRevoke() {
    try {
      await revoke.mutateAsync(order.id);
      setLink(null);
      onOpenChange(false);
      toast.success("Link je opozvan");
    } catch (e: any) {
      toast.error(e?.message || "Opoziv nije uspio");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Podijeli predračun</DialogTitle>
          <DialogDescription>
            Partner otvara link u pregledniku i preuzima predračun kao PDF. Prijava nije potrebna.
          </DialogDescription>
        </DialogHeader>

        {!link ? (
          <div className="flex items-center gap-2 py-6 text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Pravim link…
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Input readOnly value={link.url} onFocus={(e) => e.currentTarget.select()} className="tabular-nums" />
              <Button variant="outline" size="icon-sm" aria-label="Kopiraj link" onClick={() => copy(link.url, "Link kopiran")}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button className="gap-1.5" onClick={() => { window.location.href = viberForwardUrl(message); }}>
                <MessageCircle className="h-3.5 w-3.5" />
                Viber
              </Button>
              <Button
                variant="outline"
                className="gap-1.5"
                onClick={() => { window.location.href = mailtoUrl(partnerEmail, order.number, message); }}
              >
                <Mail className="h-3.5 w-3.5" />
                Email
              </Button>
            </div>

            {/* Viber se otvara samo ako je instaliran; poruka mora biti dohvatljiva i bez njega. */}
            <button
              type="button"
              className="text-left text-xs text-muted-foreground hover:text-foreground hover:underline underline-offset-2"
              onClick={() => copy(message, "Poruka kopirana")}
            >
              Kopiraj cijelu poruku
            </button>

            <div className="flex items-center justify-between gap-2 border-t pt-3">
              <span className="text-xs text-muted-foreground">Link vrijedi do {fmtUnixDate(Math.floor(link.expiresAt / 1000))}</span>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-destructive hover:text-destructive"
                disabled={revoke.isPending}
                onClick={handleRevoke}
              >
                <Link2Off className="h-3.5 w-3.5" />
                Opozovi
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
